import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface NavigationContextType {
  previousPath: string;
  goBack: () => void;
  updateProgress: (progress: number) => void;
  currentProgress: number;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [previousPath, setPreviousPath] = useState<string>("");
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== previousPath && previousPath !== "") {
      sessionStorage.setItem("previousPath", previousPath);
    }
    setPreviousPath(location.pathname);
  }, [location, previousPath]);

  const goBack = () => {
    if (previousPath && previousPath !== location.pathname) {
      navigate(previousPath);
    } else {
      const fallbackPath = sessionStorage.getItem("previousPath");
      if (fallbackPath && fallbackPath !== location.pathname) {
        navigate(fallbackPath);
      } else {
        navigate("/");
      }
    }
  };

  const updateProgress = (progress: number) => {
    setCurrentProgress(progress);
    sessionStorage.setItem("navigationProgress", String(progress));
  };

  return (
    <NavigationContext.Provider 
      value={{ 
        previousPath, 
        goBack, 
        updateProgress, 
        currentProgress,
        isLoading,
        setIsLoading
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};